/* SIZESOFTX
=========================================================================================
* Descripción: Modal de búsqueda de proveedores
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador             | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1        | 14/05/2026 |                           | Creación catélogo proveedores
*      2        | 14/05/2026 |                           | Ajuste uso correcto de GetRequest y API paginada
*      3        | 14/05/2026 |                           | Integración con context para seguir arquitectura del sistema
*      4        | 14/05/2026 |                           | Arquitectura simplificada como ModalBuscarArticulosSimple
* ---------------------------------------------------------------------------------------
*/

import TextField from "../../ui/TextField";
import React, { useState, useEffect, useRef } from "react";
import { Modal, Box, IconButton, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

import { useProveedores } from "../../../context/Catalogos/ProveedoresContext";

const modalStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '75vw',
    maxWidth: '900px',
    height: '75vh',
    bgcolor: 'background.paper',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    borderRadius: '8px',
    p: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
};

export default function ModalBuscarProveedores({
    openModal,
    handleCloseModal,
    onSelectProveedor
}) {
    const { handleBuscarProveedoresFiltro } = useProveedores();

    const inputRef = useRef(null);
    const tablaRef = useRef(null);

    const persistRef = useRef({
        filtro: "",
        lista: [],
        indexSel: -1
    });

    const [filtro, setFiltro] = useState(persistRef.current.filtro);
    const [lista, setLista] = useState(persistRef.current.lista);
    const [indexSel, setIndexSel] = useState(persistRef.current.indexSel);
    const [proveedorSeleccionado, setProveedorSeleccionado] = useState({});

    useEffect(() => {
        persistRef.current = { filtro, lista, indexSel };
    }, [filtro, lista, indexSel]);

    useEffect(() => {
        if (openModal) {
            setFiltro(persistRef.current.filtro);
            setLista(persistRef.current.lista);
            setIndexSel(persistRef.current.indexSel);
            setProveedorSeleccionado({});

            requestAnimationFrame(() => {
                if (persistRef.current.indexSel >= 0) {
                    tablaRef.current?.focus();
                    scrollToIndex(persistRef.current.indexSel);
                } else {
                    inputRef.current?.focus();
                    inputRef.current?.select();
                }
            });
        }
    }, [openModal]);

    const scrollToIndex = (index) => {
        const container = tablaRef.current;
        const rows = container?.querySelectorAll(".row-proveedor");

        if (!rows || !rows[index]) return;

        const row = rows[index];
        const rowTop = row.offsetTop;
        const rowBottom = rowTop + row.offsetHeight;

        const viewTop = container.scrollTop;
        const viewBottom = viewTop + container.clientHeight;

        if (rowBottom > viewBottom) {
            container.scrollTop = rowBottom - container.clientHeight;
        }

        if (rowTop < viewTop) {
            container.scrollTop = rowTop;
        }
    };

    const buscar = async () => {
        if (!filtro) return;

        const result = await handleBuscarProveedoresFiltro(filtro, 0);

        setLista(result.lista);

        if (result.lista.length > 0) {
            setIndexSel(0);
            setProveedorSeleccionado(result.lista[0]);

            setTimeout(() => {
                tablaRef.current?.focus();
                scrollToIndex(0);
            }, 50);
        } else {
            setIndexSel(-1);
            setProveedorSeleccionado({});
        }
    };

    const handleKeyDownInput = async (e) => {
        if (e.key === "Enter") {
            await buscar();
        }

        if (e.key === "ArrowDown") {
            if (lista.length > 0) {
                e.preventDefault();
                setIndexSel(0);
                setTimeout(() => {
                    tablaRef.current?.focus();
                    scrollToIndex(0);
                }, 10);
            }
        }
    };

    const handleKeyDownTabla = async (e) => {
        if (!lista.length) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            const next = indexSel + 1;
            if (next < lista.length) {
                setIndexSel(next);
                setProveedorSeleccionado(lista[next]);
                scrollToIndex(next);
            }
        }

        if (e.key === "ArrowUp") {
            e.preventDefault();
            if (indexSel > 0) {
                const prev = indexSel - 1;
                setIndexSel(prev);
                setProveedorSeleccionado(lista[prev]);
                scrollToIndex(prev);
            } else {
                tablaRef.current?.blur();
                inputRef.current?.focus();
                inputRef.current?.select();
                return;
            }
        }

        if (e.key === 'Enter') {
            e.preventDefault();
            if (proveedorSeleccionado && proveedorSeleccionado.claveproveedor) {
                onSelectProveedor(proveedorSeleccionado);
                handleCloseModal();
            }
            return;
        }

        if (e.key === "Escape") {
            handleCloseModal();
        }
    };

    const handleDoubleClickRow = (proveedor) => {
        if (!proveedor) return;
        onSelectProveedor(proveedor);
        handleCloseModal();
    };

    const headerStyle = {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        backgroundColor: "#23165d",
        color: "#fff",
        borderRadius: "8px 8px 0 0"
    };

    if (!openModal) return null;

    return (
        <Modal open={openModal} onClose={handleCloseModal}>
            <Box sx={modalStyle}>
                <div style={headerStyle}>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                        Buscar Proveedor
                    </Typography>
                    <IconButton onClick={handleCloseModal} sx={{ color: '#fff' }}>
                        <CloseIcon />
                    </IconButton>
                </div>

                <div style={{ padding: '16px', borderBottom: '1px solid #eee' }}>
                    <TextField
                        inputRef={inputRef}
                        label="Filtro (Clave, Razón Social, Nombre Corto)"
                        value={filtro}
                        onChange={(e) => setFiltro(e.target.value)}
                        onKeyDown={handleKeyDownInput}
                        autoComplete="off"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 400 }}
                    />
                </div>

                <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", padding: '0 8px' }}>
                    <div
                        ref={tablaRef}
                        tabIndex={0}
                        onKeyDown={handleKeyDownTabla}
                        style={{ flex: 1, overflowY: "auto" }}
                    >
                        <TableContainer component={Paper} sx={{ maxHeight: '100%' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '100px' }}>Clave</TableCell>
                                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '150px' }}>Nombre Corto</TableCell>
                                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Razón Social</TableCell>
                                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '120px' }}>Teléfono</TableCell>
                                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '120px' }}>Ciudad</TableCell>
                                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Email</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {lista.length > 0 ? (
                                        lista.map((proveedor, index) => (
                                            <TableRow
                                                key={index}
                                                className="row-proveedor"
                                                onClick={() => { setIndexSel(index); setProveedorSeleccionado(proveedor); }}
                                                onDoubleClick={() => handleDoubleClickRow(proveedor)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    backgroundColor: proveedor.claveproveedor === proveedorSeleccionado?.claveproveedor ? '#e3f2fd' : 'white',
                                                    '&:hover': { backgroundColor: '#f5f5f5' }
                                                }}
                                            >
                                                <TableCell>{proveedor.claveproveedor}</TableCell>
                                                <TableCell>{proveedor.nombrecorto}</TableCell>
                                                <TableCell>{proveedor.razonsocial}</TableCell>
                                                <TableCell>{proveedor.telefono1}</TableCell>
                                                <TableCell>{proveedor.ciudad}</TableCell>
                                                <TableCell>{proveedor.mail}</TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#666' }}>
                                                {filtro ? "Sin resultados" : "Escribe y presiona Enter"}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                </div>
            </Box>
        </Modal>
    );
}